import { QuestionCircleOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Form,
  InputNumber,
  message,
  Modal,
  Row,
  Spin,
  Tag,
  Tooltip,
} from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import LinkAddressCopy from 'component/link/link-address-copy'
import { FEATURE } from 'constants/feature'
import { TokenizeStatusEnum } from 'constants/pto'
import {
  PTO_V3_ON_SALE_POLL_ATTEMPTS,
  PTO_V3_ON_SALE_POLL_INTERVAL_MS,
  PTO_V3_POOL_ERROR_MESSAGES,
  PTO_V3_SELLING_STATUS_CONFIG,
  PtoV3SellingStatusEnum,
} from 'constants/pto-v3'
import { usePTOV3Context } from 'context/pto-v3-context-provider'
import { describeV3ContractError, initTimeLinesV3, toPhasesV3 } from 'extends/pto-v3.extend'
import { useCreatePoolDistributionV3 } from 'hooks/pto-v3/use-create-pool-distribution-v3'
import { usePoolInfoV3 } from 'hooks/pto-v3/use-pool-info-v3'
import { ORCHESTRATOR_NOT_CONFIGURED } from 'hooks/pto-v3/use-pto-orchestrator'
import { useVaultBalanceV3 } from 'hooks/pto-v3/use-vault-balance-v3'
import { useVaultStatusV3 } from 'hooks/pto-v3/use-vault-status-v3'
import { usePermission } from 'hooks/use-permission'
import ButtonUploadWhitelist from 'modules/pto-v3/form/button-upload-whitelist'
import Timelines from 'modules/pto-v3/form/timelines'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { getApiErrorCode, getApiErrorMessage } from 'utils/api-error'
import { formatNumber } from 'utils/formatted-number'
import { logError, logInfo } from 'utils/logger'

import ButtonDeployContract from '../button-deploy-contract'

const MIN_PHASES = 2
const MAX_PHASES = 6

/**
 * PTO v3 - Selling Details tab (PTO_V3_CREATE_FLOW_FE.md):
 *  2. Price per token (USDC) + Transaction Fee (%) + Timelines + Whitelist upload
 *     -> PATCH /admin/pto/v3/:id/selling-details (phases use the v3 names; the BE numbers phaseId)
 *  5-6. Create Pool -> GET create-pool-distribution?pricePerPTO&buyingFeeBP
 *     -> PTOOrchestrator.createDistributionPool (BE stores poolId / vault from the events;
 *     the contracts fund the vault with PTOs in the same flow - no admin mint step)
 *  9. Sync vault status -> BE flips the PTO to ON_SALE once the vault is verified and funded
 *
 * One open pool per PTO: End Pool (PTODistribution.endPool) before creating a new one.
 */
const SellingDetails = () => {
  const [form] = Form.useForm()
  const [editing, setEditing] = useState(false)
  const [waitingOnSale, setWaitingOnSale] = useState(false)
  const pollRef = useRef(0)

  const { address } = useAccount()
  const { code, ptoDetail, contractAddress, getPTODetail } = usePTOV3Context()
  const { isAgent, orchestratorAddress, createPoolDistribution, reportPoolCreated } =
    useCreatePoolDistributionV3()
  const { vaultBalance, mintedSupply, getVaultBalance } = useVaultBalanceV3(
    contractAddress,
    ptoDetail?.vaultAddress
  )
  const {
    poolInfo,
    distributionAddress,
    loading: poolLoading,
    getPoolInfo,
    endPool,
  } = usePoolInfoV3(contractAddress)
  const { vaultStatus, syncing, syncVaultStatus } = useVaultStatusV3(code)
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.PTO)

  const sellingStatus: PtoV3SellingStatusEnum =
    ptoDetail?.sellingStatus ?? PtoV3SellingStatusEnum.OFF_SALE
  const sellingStatusConfig =
    PTO_V3_SELLING_STATUS_CONFIG[sellingStatus] ||
    PTO_V3_SELLING_STATUS_CONFIG[PtoV3SellingStatusEnum.OFF_SALE]

  const hasContract = !!contractAddress
  const hasVault = !!ptoDetail?.vaultAddress
  const hasOpenPool = !!poolInfo && !poolInfo.isEnd
  // any pool, open or ended: phaseIds are numbered per pool and baked on-chain, so the BE
  // asks to lock price / fee / phases once a pool exists (PTO_DISTRIBUTION_UPGRADE_FE.md)
  const hasPool = !!poolInfo || (ptoDetail?.poolIds?.length || 0) > 0
  const pricePerPTO = Number(ptoDetail?.pricePerPTO || 0)
  const buyingFeeBP = Number(ptoDetail?.buyingFeeBP || 0)
  const hasPrice = pricePerPTO > 0
  const hasPhases = (ptoDetail?.phases?.length || 0) >= MIN_PHASES
  const cap = Number(ptoDetail?.maxNFTSupply || 0)
  const remainingSupply = mintedSupply === null ? null : Math.max(cap - mintedSupply, 0)
  // price / fee / phases are frozen once a pool exists (they are baked into the pool)
  const editable = !hasPool && editing
  // the whitelist (wallets eligible in the whitelist phase) can be uploaded while editing
  // and stays open once the pool is live, like v2
  const whitelistEdit = canEdit && (editable || hasOpenPool)

  const initForm = useCallback(() => {
    if (!ptoDetail) return
    form.setFieldsValue({
      // token cap - the API keeps the v1/v2 name maxNFTSupply
      totalSupply: ptoDetail?.maxNFTSupply,
      pricePerPTO: ptoDetail?.pricePerPTO,
      transactionFee: ptoDetail?.buyingFeeBP,
      phases: initTimeLinesV3(ptoDetail?.phases),
    })
  }, [form, ptoDetail])

  useEffect(() => {
    if (!editing) {
      initForm()
    }
  }, [initForm, editing])

  const handleCancel = () => {
    setEditing(false)
    initForm()
  }

  useEffect(() => {
    return () => {
      pollRef.current += 1
    }
  }, [])

  /**
   * After createDistributionPool the BE hook flips the PTO to ON_SALE within seconds.
   * Poll the detail for a while; the Sync Vault Status button stays as the manual fallback.
   */
  const waitForOnSale = async () => {
    const token = ++pollRef.current
    setWaitingOnSale(true)
    try {
      for (let attempt = 0; attempt < PTO_V3_ON_SALE_POLL_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, PTO_V3_ON_SALE_POLL_INTERVAL_MS))
        if (pollRef.current !== token) return
        const res = await AppAdmin.getPTOV3Detail(code as string)
        const status = res.data?.data?.sellingStatus
        if (status === PtoV3SellingStatusEnum.ON_SALE) {
          message.success('PTO is On Sale')
          getPTODetail()
          getVaultBalance()
          return
        }
      }
      if (pollRef.current === token) {
        message.warning(
          'The PTO is not On Sale yet. Use "Sync Vault Status" to check the vault on-chain.'
        )
        getPTODetail()
      }
    } catch (error) {
      logError('Polling the PTO status failed:', error)
    } finally {
      if (pollRef.current === token) setWaitingOnSale(false)
    }
  }

  /** Step 2 - PATCH /admin/pto/v3/:id/selling-details (raw units, v3 phase names) */
  const handleSaveSellingDetails = async () => {
    try {
      const values = await form.validateFields()
      const phases = toPhasesV3(values.phases)
      if (phases.length < MIN_PHASES || phases.length > MAX_PHASES) {
        message.error(`Please enable between ${MIN_PHASES} and ${MAX_PHASES} phases!`)
        return
      }
      onOpenModalProcessing()
      await AppAdmin.ptoV3UpdateSellingDetails(code as string, {
        pricePerPTO: values.pricePerPTO,
        buyingFeeBP: values.transactionFee,
        phases,
      })
      message.success('Selling details saved!')
      setEditing(false)
      getPTODetail()
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Error while saving selling details'))
      logError('Error while saving selling details:', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  /** Step 5 + 6 - vault + pool on-chain; the BE stores the result from the events */
  const handleCreatePool = async () => {
    if (!address) {
      message.error('Please connect wallet!')
      return
    }
    if (isAgent === false) {
      message.error('Your wallet is not an agent of the PTO Orchestrator!')
      return
    }
    if (!hasContract) {
      message.error('The PTO contract has not been deployed yet!')
      return
    }
    if (!hasPrice || !hasPhases) {
      message.error('Please save the price per token and the timelines first!')
      return
    }
    try {
      onOpenModalProcessing()
      const result = await createPoolDistribution(address, code as string, {
        pricePerPTO,
        buyingFeeBP,
      })
      logInfo('Pool distribution created:', result)
      await reportPoolCreated(
        code as string,
        result,
        contractAddress,
        ptoDetail?.status ?? TokenizeStatusEnum.ACTIVE
      )
      message.success('Pool created and vault funded! Waiting for the PTO to go On Sale...')
      getPTODetail()
      getPoolInfo()
      getVaultBalance()
      waitForOnSale()
    } catch (error) {
      // API pre-checks (400 PTO_ORCHESTRATOR_NOT_AGENT / PTO_SUPPLY_EXHAUSTED / PTO_OWNER_INVALID,
      // 409 PTO_POOL_STILL_OPEN, 500 wiring) and contract reverts both end up here -
      // nothing was sent in the API cases
      const code = getApiErrorCode(error)
      const friendly = code ? PTO_V3_POOL_ERROR_MESSAGES[code] : undefined
      message.error(
        friendly ||
          describeV3ContractError(error) ||
          getApiErrorMessage(error, 'Error while creating the pool')
      )
      logError('Error while creating the pool:', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  /** PTODistribution.endPool - permanent, required before a new pool can be created */
  const handleEndPool = () => {
    if (!address || !poolInfo) return
    Modal.confirm({
      title: `End pool #${poolInfo.poolId}?`,
      content:
        'Ending a pool is permanent: it cannot be reopened and the PTO goes Off Sale. A new pool can be created afterwards.',
      okText: 'End Pool',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      centered: true,
      onOk: async () => {
        try {
          onOpenModalProcessing()
          const { txHash } = await endPool(address, poolInfo.poolId)
          logInfo('Pool ended, tx:', txHash)
          message.success(`Pool #${poolInfo.poolId} ended`)
          getPTODetail()
        } catch (error) {
          const errorMessage = describeV3ContractError(error)
          message.error(errorMessage || 'Error while ending the pool')
          logError('Error while ending the pool:', error)
        } finally {
          onCloseModalProcessing()
        }
      },
    })
  }

  /** Step 9 - ask the BE to check the vault now (verified + balance -> ON_SALE) */
  const handleSyncVaultStatus = async (silent = false) => {
    try {
      const status = await syncVaultStatus()
      if (!status) return
      if (!status.isVerified) {
        message.warning(
          'The vault is NOT verified in the Identity Registry: buys will revert. Contact the BE / contract team.'
        )
      } else if (!silent) {
        message.success(
          status.ready ? 'Vault is ready, PTO is On Sale' : 'Vault is verified but empty'
        )
      }
      getPTODetail()
      getVaultBalance()
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Error while syncing the vault status'))
    }
  }

  const createPoolBlocker = !orchestratorAddress
    ? ORCHESTRATOR_NOT_CONFIGURED
    : !hasContract
      ? 'Deploy the PTO contract first'
      : !hasPrice || !hasPhases
        ? 'Save the price per token and the timelines first'
        : isAgent === false
          ? 'Connected wallet is not an orchestrator agent'
          : hasOpenPool
            ? 'End the open pool before creating a new one'
            : ''

  return (
    <div className="p2p-create">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveSellingDetails}
        scrollToFirstError={{
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        }}
      >
        {/* Hidden - used by the timelines validators */}
        <Form.Item name="totalSupply" hidden>
          <InputNumber />
        </Form.Item>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 10,
          }}
        >
          <div>
            Selling Status:&nbsp;
            <Tag color={sellingStatusConfig.color}>{sellingStatusConfig.label}</Tag>
          </div>
          <div>
            {canEdit && !hasPool && !editing ? (
              <Button type="primary" onClick={() => setEditing(true)}>
                Edit Selling Details
              </Button>
            ) : null}
            {editing ? (
              <>
                <Button style={{ marginRight: 15 }} onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Save
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <fieldset>
          <legend>Selling Information</legend>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Price per token
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Price of one whole PTO in USDC (at most 6 decimals). It is baked into
                            the pool when the pool is created.
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="pricePerPTO"
                rules={[
                  { required: true, message: 'Please input the price per token!' },
                  {
                    type: 'number',
                    min: 0.000001,
                    message: 'Price per token must be greater than or equal to 0.000001!',
                  },
                ]}
              >
                <InputNumber
                  style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
                  min={0.000001}
                  placeholder="Enter the price per token"
                  disabled={!editable}
                  addonBefore={'$'}
                  addonAfter={'USDC'}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Transaction Fee
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Buying fee in percent (0 to below 100), converted to basis points by the
                            BE for the pool
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="transactionFee"
                rules={[
                  { required: true, message: 'Please input the transaction fee!' },
                  {
                    type: 'number',
                    min: 0,
                    max: 99.99,
                    message: 'Transaction fee must be between 0 and below 100!',
                  },
                ]}
              >
                <InputNumber
                  style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
                  min={0}
                  max={99.99}
                  placeholder="Enter the transaction fee"
                  disabled={!editable}
                  addonAfter={'%'}
                />
              </Form.Item>
            </Col>
          </Row>
        </fieldset>

        <Timelines form={form} editable={editable} />

        <fieldset>
          <legend>Whitelist</legend>
          <ButtonUploadWhitelist id={code} edit={whitelistEdit} />
        </fieldset>
      </Form>

      <fieldset>
        <legend>Pool & Vault</legend>
        {!orchestratorAddress ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={ORCHESTRATOR_NOT_CONFIGURED}
          />
        ) : null}
        {!hasContract ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Deploy the PTO contract before creating the pool."
            action={
              canEdit ? (
                <ButtonDeployContract
                  id={code as string}
                  onSuccess={() => {
                    getPTODetail()
                  }}
                />
              ) : undefined
            }
          />
        ) : null}
        <Spin spinning={poolLoading}>
          <Descriptions column={2} bordered size="small" style={{ marginBottom: 12 }}>
            <Descriptions.Item label="PTO Address">
              <LinkAddressCopy address={contractAddress} />
            </Descriptions.Item>
            <Descriptions.Item label="PTO OnchainID">
              <LinkAddressCopy address={ptoDetail?.onchainId} />
            </Descriptions.Item>
            <Descriptions.Item label="Vault Address">
              <LinkAddressCopy address={ptoDetail?.vaultAddress} />
            </Descriptions.Item>
            <Descriptions.Item label="Vault OnchainID">
              <LinkAddressCopy address={ptoDetail?.vaultIdentity} />
            </Descriptions.Item>
            <Descriptions.Item label="Vault balance (PTO)">
              {vaultBalance === null ? '-' : vaultBalance}
            </Descriptions.Item>
            <Descriptions.Item label="Minted / cap">
              {mintedSupply === null ? '-' : `${mintedSupply} / ${cap}`}
              {remainingSupply !== null ? ` (${remainingSupply} left for the next pool)` : ''}
            </Descriptions.Item>
          </Descriptions>
          {poolInfo ? (
            <Descriptions
              title="On-chain pool"
              column={3}
              bordered
              size="small"
              style={{ marginBottom: 12 }}
            >
              <Descriptions.Item label="Pool ID">
                #{poolInfo.poolId}
                {distributionAddress ? (
                  <span style={{ marginLeft: 8, color: '#8c8c8c' }}>
                    on <LinkAddressCopy address={distributionAddress} max={6} />
                  </span>
                ) : null}
              </Descriptions.Item>
              <Descriptions.Item label="Price per token">
                {formatNumber(poolInfo.pricePerPTO, 2)} USDC
              </Descriptions.Item>
              <Descriptions.Item label="Buying fee">{poolInfo.buyingFee}%</Descriptions.Item>
              <Descriptions.Item label="Max PTO amount">{poolInfo.maxPTOAmount}</Descriptions.Item>
              <Descriptions.Item label="Purchased / Distributed">
                {poolInfo.totalPTOsPurchase} / {poolInfo.totalPTODistributed}
              </Descriptions.Item>
              <Descriptions.Item label="Raised (USDC)">
                {formatNumber(poolInfo.totalLoanAmountRaised, 3)} (fee{' '}
                {formatNumber(poolInfo.totalFeeCollected, 3)})
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {poolInfo.isEnd ? (
                  <Tag color="default">Ended</Tag>
                ) : (
                  <Tag color="processing">Open</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Spin>
        {canEdit ? (
          <div style={{ display: 'flex', gap: 10 }}>
            {!hasOpenPool ? (
              <Tooltip title={createPoolBlocker}>
                <Button
                  type="primary"
                  onClick={handleCreatePool}
                  disabled={!!createPoolBlocker || editing || poolLoading}
                >
                  Create Pool
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={isAgent === false ? 'Connected wallet is not an agent' : ''}>
                <Button
                  danger
                  onClick={handleEndPool}
                  disabled={!address || isAgent === false || poolLoading}
                >
                  End Pool
                </Button>
              </Tooltip>
            )}
            <Button
              onClick={() => handleSyncVaultStatus()}
              loading={syncing || waitingOnSale}
              disabled={!hasVault}
            >
              {waitingOnSale ? 'Waiting for On Sale...' : 'Sync Vault Status'}
            </Button>
          </div>
        ) : null}
        {vaultStatus ? (
          <Descriptions
            title="Vault status"
            column={4}
            bordered
            size="small"
            style={{ marginTop: 12 }}
          >
            <Descriptions.Item label="Verified">
              {vaultStatus.isVerified ? (
                <Tag color="success">Yes</Tag>
              ) : (
                <Tag color="error">No - buys will revert</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Balance (PTO)">{vaultStatus.balance}</Descriptions.Item>
            <Descriptions.Item label="Ready">
              {vaultStatus.ready ? <Tag color="success">Ready</Tag> : <Tag>Not yet</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Checked at">
              {vaultStatus.checkedAt ? new Date(vaultStatus.checkedAt).toLocaleString() : '-'}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </fieldset>
    </div>
  )
}

export default SellingDetails
